import apiClient from './client';
import type {
  SessionResponse,
  StartSessionRequest,
  SubmitAnswerRequest,
  SubmitAnswerResponse,
  ReviewProblemsResponse,
  ReviewActionResponse,
  SessionFeatureKey,
} from '../types/problems';

export const problemsApi = {
  startSession: async (request: StartSessionRequest): Promise<SessionResponse> => {
    const response = await apiClient.post<SessionResponse>('/problems/session', request);
    return response.data;
  },

  submitAnswer: async (problemId: number, request: SubmitAnswerRequest): Promise<SubmitAnswerResponse> => {
    const response = await apiClient.post<SubmitAnswerResponse>(
      `/problems/${problemId}/answer`,
      request
    );
    return response.data;
  },

  skipProblem: async (
    problemId: number,
    params?: { sessionOrigin?: string },
  ): Promise<void> => {
    // v1.8: sessionOrigin lets the backend honour the same history gate as /answer. (The
    // feature 008 v2 `kind` query parameter is no longer sent — feature 033 made the overcalling
    // page problems-only; the server still accepts it.)
    const query = new URLSearchParams();
    if (params?.sessionOrigin) query.set('sessionOrigin', params.sessionOrigin);
    const qs = query.toString();
    await apiClient.post(`/problems/${problemId}/skip${qs ? `?${qs}` : ''}`);
  },

  /**
   * Get Important Sequences problems (v1.4).
   * T094: Returns unseen problems with "important" in their problem-Components.
   * Backend returns HTTP 200 with empty array if no problems (correct REST semantics).
   */
  getImportantSequences: async (): Promise<SessionResponse> => {
    const response = await apiClient.get<SessionResponse>('/problems/important-sequences');
    return response.data;
  },

  /**
   * Get Overcalling problems.
   * Returns problems where the opponent made the opening bid.
   * Backend returns HTTP 200 with empty array if no problems available.
   */
  getOvercalling: async (): Promise<SessionResponse> => {
    const response = await apiClient.get<SessionResponse>('/problems/overcalling');
    return response.data;
  },

  /**
   * Get user's review problems (v1.4).
   * T110: Returns all problems in the user's review list.
   * Backend returns HTTP 200 with empty array if no problems (correct REST semantics).
   */
  getReviewProblems: async (): Promise<ReviewProblemsResponse> => {
    const response = await apiClient.get<ReviewProblemsResponse>('/problems/review');
    return response.data;
  },

  /**
   * Count of problems waiting in the user's review set (feature 023, FR-007).
   * Powers the dashboard "My Review Problems" badge, which is hidden at 0.
   */
  getReviewCount: async (): Promise<number> => {
    const response = await apiClient.get<{ count: number }>('/problems/review/count');
    return response.data.count;
  },

  /**
   * The problem ids already in the user's review list (feature 023, 2026-08-03).
   * Read once per practice page so the pin opens as "In Review" for a problem pinned in an
   * earlier session — ids only, since membership is the whole question being asked.
   */
  getReviewProblemIds: async (): Promise<number[]> => {
    const response = await apiClient.get<{ problemIds: number[] }>('/problems/review/ids');
    return response.data.problemIds;
  },

  /**
   * Add a problem to the user's review list (v1.4).
   * T101: Saves problem for later review.
   */
  addToReview: async (problemId: number): Promise<ReviewActionResponse> => {
    const response = await apiClient.post<ReviewActionResponse>(`/problems/${problemId}/review`);
    return response.data;
  },

  /**
   * Remove a problem from the user's review list (v1.4).
   * T110: Removes problem from review list.
   */
  removeFromReview: async (problemId: number): Promise<ReviewActionResponse> => {
    const response = await apiClient.delete<ReviewActionResponse>(`/problems/${problemId}/review`);
    return response.data;
  },

  /**
   * Feature 026 — get the caller's saved resume position for a practice feature.
   * Returns the question number reached (0 when nothing is stored, so no prompt is shown).
   */
  getSessionPosition: async (feature: SessionFeatureKey): Promise<number> => {
    const response = await apiClient.get<{ featureKey: string; questionNumber: number }>(
      `/problems/session-position/${feature}`,
    );
    return response.data.questionNumber;
  },

  /**
   * Feature 026 — save (upsert) the caller's resume position for a practice feature.
   * Called each time a problem is dealt with; questionNumber is the count dealt with so far.
   */
  saveSessionPosition: async (feature: SessionFeatureKey, questionNumber: number): Promise<void> => {
    await apiClient.put(`/problems/session-position/${feature}`, { questionNumber });
  },

  /**
   * Feature 026 — clear the caller's saved resume position for a practice feature.
   * Called on "start from the beginning", on completing the set, and on invalidation.
   */
  clearSessionPosition: async (feature: SessionFeatureKey): Promise<void> => {
    await apiClient.delete(`/problems/session-position/${feature}`);
  },

  /**
   * Get a problem by its serial number (dev-only QA feature).
   * Returns a SessionResponse with a single problem.
   */
  getProblemBySerial: async (serialNumber: number): Promise<SessionResponse> => {
    const response = await apiClient.get<SessionResponse>(`/problems/by-serial/${serialNumber}`);
    return response.data;
  },
};
